import React, { useContext, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
// Barrel import on purpose: a deep '@mui/material/GlobalStyles' import is a
// new dep-optimizer entry for this app and esbuild pre-bundles it with
// broken emotion interop ("styled_default is not a function" in dev).
import { GlobalStyles } from '@mui/material';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { getMuiTheme } from './muiTheme';
import legacyTheme from './theme';
import { GlobalContext } from './utils/ContextProviders/GlobalContextProvider';

// App-level theming driven by GlobalContext's darkMode. Mounted once in
// App.tsx so every route — including /login, which renders outside
// Component/layout.js — gets both the MUI theme and the legacy
// styled-components theme. layout.js still mounts its own providers with
// the same values, which is harmless duplication until it is retired.
//
// Deliberately GlobalStyles rather than CssBaseline: CssBaseline applies a
// global box-sizing: border-box reset that legacy pages were not written
// against. Here we only theme the body background (index.css hardcodes a
// light one) and color-scheme (so scrollbars/form controls follow the mode).
const AppThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { darkMode } = useContext(GlobalContext);
  const mode = darkMode ? 'dark' : 'light';
  const muiTheme = useMemo(() => getMuiTheme(mode), [mode]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <StyledThemeProvider
        theme={darkMode ? legacyTheme.dark : legacyTheme.light}
      >
        <GlobalStyles
          styles={{
            body: {
              backgroundColor: muiTheme.palette.background.default,
              // MUI Typography inherits color, so unstyled text falls back to
              // the body color — without this it stays browser-default black
              // in dark mode.
              color: muiTheme.palette.text.primary,
              colorScheme: mode,
              // Bridge for SCSS files, which can't read the MUI theme:
              // rules there use var(--rb-*, <legacy hex>) so light mode
              // stays pixel-identical while dark mode follows the palette.
              '--rb-bg-paper': muiTheme.palette.background.paper,
              '--rb-bg-default': muiTheme.palette.background.default,
              '--rb-bg-hover': muiTheme.palette.action.hover,
              '--rb-text-primary': muiTheme.palette.text.primary,
              '--rb-text-secondary': muiTheme.palette.text.secondary,
              '--rb-text-disabled': muiTheme.palette.text.disabled,
              '--rb-divider': muiTheme.palette.divider
            }
          }}
        />
        {children}
      </StyledThemeProvider>
    </MuiThemeProvider>
  );
};

export default AppThemeProvider;
